/**
 * Remastered lighting (docs/spec/04 §8): the cave sits in a gentle gloom, and light comes from what
 * should give it - Digby's helmet lamp, glinting diamonds, fireflies, explosions, the open exit.
 * Only ever a mood: the darkest corner stays clearly readable, so it never changes the game.
 */

import { E } from '../../sim/elements';
import type { CaveState } from '../../sim/types';
import { DX, DY } from '../../sim/types';
import { makeCanvas } from '../canvas';
import { KEY_COLOURS, rgb } from '../palette';
import type { Screen } from '../screen';
import { CELL } from '../sprites/types';
import { hash3 } from './shade';

export interface LightView {
  readonly top: number;
  readonly viewW: number;
  readonly viewH: number;
  readonly camX: number;
  readonly camY: number;
  readonly anim: number;
  /** 0..1 through the tick, for things sliding between cells. */
  readonly t: number;
  readonly smooth: boolean;
  /** Digby's drawn position in cave pixels. */
  readonly digby: { x: number; y: number };
}

interface Light {
  x: number;
  y: number;
  radius: number;
  /** 0..1: how much of the gloom it lifts at its centre. */
  lift: number;
  /** Additive colour and its strength at the centre. */
  glow: readonly [number, number, number];
  glowAlpha: number;
}

const AMBIENT = 0.46;
let gloom: HTMLCanvasElement | null = null;

function flicker(x: number, y: number, anim: number, amount: number): number {
  return 1 - amount + ((hash3(x, y, anim >> 1) % 100) / 100) * amount;
}

function lightFor(el: number, s: CaveState, x: number, y: number, anim: number): Omit<Light, 'x' | 'y'> | null {
  if (el === E.DIAMOND || el === E.DIAMOND_FALLING) {
    return { radius: 13, lift: 0.5 * flicker(x, y, anim, 0.5), glow: [255, 255, 255], glowAlpha: 0.12 * flicker(y, x, anim, 0.8) };
  }
  if (el >= E.FIREFLY && el < E.FIREFLY + 4) return { radius: 34, lift: 0.85 * flicker(x, y, anim, 0.3), glow: [255, 150, 60], glowAlpha: 0.3 };
  if (el >= E.BUTTERFLY && el < E.BUTTERFLY + 4) return { radius: 22, lift: 0.55, glow: [170, 210, 255], glowAlpha: 0.14 };
  if (el >= E.EXPLODE && el < E.EXPLODE + 5) {
    const st = el - E.EXPLODE;
    return { radius: 62 - st * 9, lift: 1, glow: [255, 208, 140], glowAlpha: 0.5 - st * 0.09 };
  }
  if (el >= E.DIAMOND_BIRTH && el < E.DIAMOND_BIRTH + 5) {
    const st = el - E.DIAMOND_BIRTH;
    return { radius: 54 - st * 7, lift: 1, glow: [220, 236, 255], glowAlpha: 0.45 - st * 0.08 };
  }
  if (el >= E.BIRTH && el < E.BIRTH + 3) return { radius: 44, lift: 1, glow: [255, 230, 170], glowAlpha: 0.35 };
  if (el === E.INBOX) return { radius: 40, lift: 0.8, glow: [255, 230, 180], glowAlpha: (anim >> 3) % 2 === 0 ? 0.22 : 0.1 };
  if (el === E.OUTBOX_OPEN) return { radius: 46, lift: 0.9, glow: [200, 255, 210], glowAlpha: 0.16 + 0.1 * Math.sin(anim / 4) };
  if (el === E.MAGIC_WALL && s.magic.phase === 'active') return { radius: 18, lift: 0.5, glow: [180, 220, 255], glowAlpha: 0.12 };
  if (el >= E.KEY && el < E.KEY + 3) {
    const [r, g, b] = rgb(KEY_COLOURS[el - E.KEY] ?? 7);
    return { radius: 16, lift: 0.5, glow: [r, g, b], glowAlpha: 0.22 };
  }
  if (el === E.CLOCK) return { radius: 14, lift: 0.45, glow: [255, 240, 200], glowAlpha: 0.14 };
  return null;
}

function collect(s: CaveState, v: LightView): Light[] {
  const lights: Light[] = [];
  const margin = 4;
  const x0 = Math.max(0, Math.floor(v.camX / CELL) - margin);
  const y0 = Math.max(0, Math.floor(v.camY / CELL) - margin);
  const x1 = Math.min(s.w - 1, Math.floor((v.camX + v.viewW) / CELL) + margin);
  const y1 = Math.min(s.h - 1, Math.floor((v.camY + v.viewH) / CELL) + margin);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = y * s.w + x;
      const l = lightFor(s.cells[i] ?? 0, s, x, y, v.anim);
      if (!l) continue;
      const dir = v.smooth ? (s.moved[i] ?? 0) : 0;
      const back = dir === 0 ? 0 : 1 - v.t;
      lights.push({ ...l, x: (x - (DX[dir] ?? 0) * back) * CELL + CELL / 2, y: (y - (DY[dir] ?? 0) * back) * CELL + CELL / 2 });
    }
  }
  if (s.digby.born && s.digby.alive && s.status !== 'won') {
    lights.push({ x: v.digby.x + CELL / 2, y: v.digby.y + 3, radius: 92, lift: 1, glow: [255, 204, 140], glowAlpha: 0.18 });
  }
  return lights;
}

export function drawLighting(screen: Screen, s: CaveState, v: LightView): void {
  if (!gloom || gloom.width !== v.viewW || gloom.height !== v.viewH) gloom = makeCanvas(v.viewW, v.viewH);
  const g = gloom.getContext('2d');
  if (!g) return;
  const lights = collect(s, v);
  g.globalCompositeOperation = 'source-over';
  g.clearRect(0, 0, v.viewW, v.viewH);
  g.fillStyle = `rgba(6, 4, 20, ${AMBIENT})`;
  g.fillRect(0, 0, v.viewW, v.viewH);
  g.globalCompositeOperation = 'destination-out';
  for (const l of lights) {
    const x = l.x - v.camX;
    const y = l.y - v.camY;
    const grad = g.createRadialGradient(x, y, 0, x, y, l.radius);
    grad.addColorStop(0, `rgba(0,0,0,${l.lift})`);
    grad.addColorStop(0.55, `rgba(0,0,0,${l.lift * 0.55})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(x - l.radius, y - l.radius, l.radius * 2, l.radius * 2);
  }
  const ctx = screen.ctx;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, v.top, v.viewW, v.viewH);
  ctx.clip();
  ctx.drawImage(gloom, 0, v.top);
  ctx.globalCompositeOperation = 'lighter';
  for (const l of lights) {
    if (l.glowAlpha <= 0) continue;
    const x = l.x - v.camX;
    const y = l.y - v.camY + v.top;
    const [r, gr, b] = l.glow;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, l.radius * 0.8);
    grad.addColorStop(0, `rgba(${r},${gr},${b},${l.glowAlpha})`);
    grad.addColorStop(1, `rgba(${r},${gr},${b},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(x - l.radius, y - l.radius, l.radius * 2, l.radius * 2);
  }
  ctx.restore();
}
